import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from 'react-helmet';
import type { ReactNode } from 'react';

import Button from '/shared/components/widgets/button/Button';

import StyledTitle from '/shared/styled/StyledTitle';
import StyledContainer from '/shared/styled/StyledContainer';
import StyledInner from '/shared/styled/StyledInner';
import StyledFormActions from '/shared/styled/form/StyledFormActions';

import { developmentMode } from '/shared/util/Statics';

import ErrorComponent from './Error';
import type { DisplayError } from './Error';

function defaultRetry() {
  window.location.reload();
}

function formatDebugError(error: DisplayError) {
  if (typeof error === 'string') {
    return error;
  }
  const serialised = JSON.stringify(
    error,
    error != null && typeof error === 'object' ? Object.getOwnPropertyNames(error) : undefined,
    2,
  );
  return serialised || String(error);
}

interface Props {
  error?: DisplayError;
  children?: ReactNode;
  onRetry?: React.MouseEventHandler<HTMLButtonElement>;
}

function PageError({ error = null, children = null, onRetry = defaultRetry }: Props) {
  const { t } = useTranslation();
  return (
    <>
      <Helmet>
        <title>{t('shared:App.title', 'Carbon calculator')}</title>
      </Helmet>
      <StyledContainer $centred>
        <StyledInner $narrow>
          <StyledTitle>
            {/* @i18n: Oh no! Something went wrong */}
            <Trans i18nKey="shared:misc.somethingWentWrong" />
          </StyledTitle>
          <ErrorComponent error={error} $position="relative">
            {children}
          </ErrorComponent>
          {developmentMode && error != null && <pre>{formatDebugError(error).replace(/\\n/g, '\n')}</pre>}
          <StyledFormActions>
            <Button $primary onClick={onRetry}>
              {/* @i18n: Try again */}
              <Trans i18nKey="shared:buttons.tryAgain" />
            </Button>
          </StyledFormActions>
        </StyledInner>
      </StyledContainer>
    </>
  );
}

export default PageError;
